import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';

import { lazyGettext } from '@eb/lazy-gettext';

import { REQUIRED_CROP_CANCELED, API_RESPONSE_BAD_FORMAT } from './constants';
import { getScrollAncestor } from './get-scroll-ancestor';

const GENERIC_UPLOAD_ERROR = lazyGettext(
    'An error occurred, unable to upload at this time.',
);

const getCropMaskCoords = (cropMask) => {
    const cropMaskCoords = cropMask['top_left'] || {};
    const x =
        cropMaskCoords.x || cropMaskCoords.x === 0 ? cropMaskCoords.x : '';
    const y =
        cropMaskCoords.y || cropMaskCoords.y === 0 ? cropMaskCoords.y : '';

    return { x, y };
};

export const transformApiImageToImageField = (fieldName, apiImage) => {
    const imageObject = apiImage || {};
    const originalInfo = imageObject.original || {};
    const cropMask = imageObject['crop_mask'] || {};
    const { x, y } = getCropMaskCoords(cropMask);

    return {
        [fieldName]: imageObject.id || '',
        [`${fieldName}Url`]: imageObject.url || '',
        [`${fieldName}OriginalUrl`]: originalInfo.url || '',
        [`${fieldName}CropX`]: x,
        [`${fieldName}CropY`]: y,
        [`${fieldName}CropWidth`]: cropMask.width || '',
        [`${fieldName}CropHeight`]: cropMask.height || '',
    };
};

export const transformUploadFailureMessage = (result) => {
    if (result === REQUIRED_CROP_CANCELED) {
        return Promise.reject('');
    }
    if (result && result.json) {
        return result.json().then(({ error }) => {
            if (error === API_RESPONSE_BAD_FORMAT) {
                return Promise.reject(
                    lazyGettext(
                        'There is a problem with this image format. Please verify the image is the type you expect or choose a different image.',
                    ),
                );
            }
            return Promise.reject(GENERIC_UPLOAD_ERROR);
        });
    }
    return Promise.reject(GENERIC_UPLOAD_ERROR);
};

export const calculateWidthToPreventAncestorScroll = (
    node,
    minHeight = 400,
) => {
    const scrollAncestor = getScrollAncestor(node);

    if (!scrollAncestor) {
        return null;
    }

    let newWidth;
    const { clientWidth: nodeWidth, clientHeight: nodeHeight } = node;
    const { clientHeight: scrollableHeight, scrollHeight } = scrollAncestor;
    const overflowAmount = scrollHeight - scrollableHeight;

    if (overflowAmount > 0) {
        const newHeight = Math.max(nodeHeight - overflowAmount, minHeight);

        newWidth = Math.floor((nodeWidth * newHeight) / nodeHeight);
    }

    return newWidth;
};

// my initial need requires only one prop to deep compare.
// this could be updated to accept an array of props to deep compare,
// but it will hurt performance as it will require another loop to
// check the contents of the array.
export const conditionalDeepCompare = (objectA, objectB, deepProp) => {
    const objectAKeys = sortBy(Object.keys(objectA));
    const hasSameKeys = isEqual(objectAKeys, sortBy(Object.keys(objectB)));

    if (!hasSameKeys) {
        return false;
    }
    return objectAKeys.every((key) => {
        const comparator =
            deepProp && key === deepProp ? isEqual : (a, b) => a === b;

        return comparator(objectA[key], objectB[key]);
    });
};

export const pickPropsByTypes = (props, propTypes) =>
    pick(props, ...Object.keys(propTypes));
